import puterAI from '../lib/puter';
import { 
  getCoachingPersonality, 
  formatFeedbackWithPersonality,
  calculatePersonalityXP 
} from './lumoPersonalityService';
import aiCacheService from './aiCacheService';

// AIMLAPI configuration (OpenAI-compatible)
const AIMLAPI_KEY = import.meta.env?.VITE_AIMLAPI_KEY;
const AIMLAPI_BASE_URL = 'https://api.aimlapi.com/v1';

const isValidAPIKey = (key) => {
  if (!key) return false;
  const placeholders = ['your-aimlapi-key', 'your_aimlapi_key', 'your_api_key', 'placeholder', 'xxx', 'your-'];
  return !placeholders.some((p) => key.toLowerCase().includes(p));
};

/**
 * Service for Puter AI-powered coaching evaluation
 * Provides structured coaching feedback with AI caching for cost optimization
 * Uses database for 80% predictable responses, AI for novel cases
 */
export const puterCoachingService = {
  /**
   * Evaluate a drill response using Puter AI with caching and personality-based coaching
   * @param {Object} evaluationData - Data needed for evaluation
   * @returns {Promise<Object>} Structured evaluation result with scores and feedback
   */
  async evaluateDrillResponse(evaluationDataOrPrompt, maybeResponse, extra = {}) {
    try {
      // Backward-compatible arg handling for callers passing (prompt, response, options)
      const evaluationData = typeof evaluationDataOrPrompt === 'string'
        ? { prompt: evaluationDataOrPrompt, userResponse: maybeResponse, ...extra }
        : (evaluationDataOrPrompt || {});

      // Prefer AIMLAPI when configured
      if (AIMLAPI_KEY && isValidAPIKey(AIMLAPI_KEY)) {
        const systemPrompt = `You are a precise sales coaching evaluator. Score the response and return strict JSON only with keys: score (0-100), strengths (array), improvements (array), feedback (string), alternative (string).`;
        const userContent = `Scenario Context: ${evaluationData.scenario || evaluationData.prompt || 'Sales scenario'}\n\nUser Response: ${evaluationData.userResponse}\n\nRubric: ${JSON.stringify(evaluationData.rubric || {
          clarity: 'Was the response clear and concise?',
          effectiveness: 'Did it address the objection?'
        })}`;

        const resp = await fetch(`${AIMLAPI_BASE_URL}/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${AIMLAPI_KEY}`
          },
          body: JSON.stringify({
            model: 'gpt-4o-mini',
            temperature: 0.4,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: userContent }
            ]
          })
        });

        if (!resp.ok) {
          const t = await resp.text();
          throw new Error(`AIMLAPI error: ${resp.status} ${t}`);
        }

        const data = await resp.json();
        const content = data?.choices?.[0]?.message?.content;
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;

        // Normalize fields
        return {
          score: Math.max(0, Math.min(100, Number(parsed?.score ?? 70))),
          strengths: parsed?.strengths || [],
          improvements: parsed?.improvements || [],
          feedback: parsed?.feedback || 'Good effort. Tighten clarity and add a targeted probe.',
          alternative: parsed?.alternative || 'I hear that. Can you share what matters most in evaluating this?'
        };
      }

      if (!puterAI) {
        throw new Error('Puter AI not initialized. Please ensure Puter.js SDK is loaded.');
      }

      // Get coaching personality based on user's skill level
      const skillLevel = evaluationData?.skillLevel || 'beginner';
      const personality = getCoachingPersonality(skillLevel);
      
      // Inject personality system prompt into evaluation data
      const enhancedEvaluationData = {
        ...evaluationData,
        personalityPrompt: personality.systemPrompt,
        coachingStage: personality.name
      };

      // Try cache first, fall back to AI if miss
      const evaluation = await aiCacheService.getOrGenerate(
        evaluationData.userResponse,
        {
          promptType: 'drill_evaluation',
          industry: evaluationData.industry,
          difficultyLevel: evaluationData.difficultyLevel
        },
        async () => await puterAI?.evaluateDrillResponse(enhancedEvaluationData)
      );
      
      // Format feedback with personality templates; map to expected shape
      const formattedEvaluation = formatFeedbackWithPersonality(evaluation, skillLevel);
      return {
        score: formattedEvaluation?.scores?.overall ?? 70,
        strengths: formattedEvaluation?.strengths || [],
        improvements: formattedEvaluation?.improvements || [],
        feedback: formattedEvaluation?.summary || 'Good effort. Tighten clarity and add a targeted probe.',
        alternative: formattedEvaluation?.sampleResponse || 'I hear that. Can you share what matters most in evaluating this?'
      };
    } catch (error) {
      console.error('Error evaluating drill response with Puter AI:', error);
      
      // Return a fallback response if AIMLAPI/Puter fails
      return {
        score: 75,
        strengths: [
          'Your response shows engagement with the scenario',
          'You attempted to address the key points',
          'You provided a structured answer'
        ],
        improvements: [
          'Try to be more specific with examples or data',
          'Consider asking follow-up questions to maintain control',
          'Include a clear call-to-action or next step'
        ],
        feedback: 'Unable to generate AI evaluation at this time. Please try again.',
        alternative: 'AI evaluation temporarily unavailable. Your response has been saved.'
      };
    }
  },

  /**
   * Evaluate an objection handling response using Puter AI with caching and personality-based coaching
   * @param {Object} evaluationData - Data needed for objection evaluation
   * @returns {Promise<Object>} Structured evaluation result with scores and feedback
   */
  async evaluateObjectionResponse(evaluationData) {
    try {
      if (!puterAI) {
        throw new Error('Puter AI not initialized. Please ensure Puter.js SDK is loaded.');
      }

      // Get coaching personality based on user's skill level
      const skillLevel = evaluationData?.skillLevel || 'beginner';
      const personality = getCoachingPersonality(skillLevel);
      
      // Inject personality system prompt into evaluation data
      const enhancedEvaluationData = {
        ...evaluationData,
        personalityPrompt: personality.systemPrompt,
        coachingStage: personality.name
      };

      // Try cache first, fall back to AI if miss
      const evaluation = await aiCacheService.getOrGenerate(
        `${evaluationData.objectionScenario}__${evaluationData.userResponse}`,
        {
          promptType: 'objection_evaluation',
          industry: evaluationData.industry,
          objectionType: evaluationData.objectionType
        },
        async () => await puterAI?.evaluateObjectionResponse(enhancedEvaluationData)
      );
      
      // Format feedback with personality templates
      const formattedEvaluation = formatFeedbackWithPersonality(evaluation, skillLevel);
      
      return formattedEvaluation;
    } catch (error) {
      console.error('Error evaluating objection response with Puter AI:', error);
      
      // Return a fallback response if Puter AI fails
      return {
        scores: {
          clarity: 70,
          confidence: 70,
          control: 70,
          close: 70,
          overall: 70
        },
        summary: 'Unable to generate AI evaluation at this time. Please try again.',
        strengths: [
          'Your response shows engagement with the objection',
          'You attempted to address the customer concern',
          'You provided a thoughtful answer'
        ],
        improvements: [
          'Try to acknowledge the objection more explicitly',
          'Consider reframing the conversation to focus on value',
          'Include a clear path forward or next step'
        ],
        sampleResponse: 'AI evaluation temporarily unavailable. Your response has been saved.'
      };
    }
  }
};